'use client';

import { forwardRef } from 'react';
import { FiExternalLink } from 'react-icons/fi';

import { Link } from '@highlight-cp/core-ui';
import type { LinkProps } from '@highlight-cp/core-ui';

export type ExternalLinkProps = LinkProps;

/**
 * ### ExternalLink
 *
 * @description
 * A `Link` that leaves the app: opens in a new tab, tells crawlers not to follow it, and marks
 * itself with an external-link glyph. The glyph is only appended for plain-string children —
 * call sites that compose their own icon keep control of the row.
 *
 * `rel` and `target` are defaults, not locks; pass either to override.
 *
 * @example
 * ```tsx
 * <ExternalLink href="https://help.letshighlight.com/help/project-timelines">
 *   Learn more
 * </ExternalLink>
 * ```
 *
 * @param props - {@link ExternalLinkProps}
 * @returns React component
 */
export const ExternalLink = forwardRef<HTMLAnchorElement, ExternalLinkProps>(
  ({ children, rel = 'noreferrer nofollow', target = '_blank', ...props }, ref) => (
    <Link ref={ref} rel={rel} target={target} {...props}>
      {children}
      {typeof children === 'string' && <FiExternalLink size={14} aria-hidden />}
    </Link>
  ),
);

ExternalLink.displayName = 'ExternalLink';
